import { Fragment, useEffect, useMemo, useState } from "react";
import { createPortal } from "react-dom";
import { toast } from "react-toastify";
import config from "../../../../config.json";
import PageTitle from "../../layouts/PageTitle";
import {
  deleteLostFoundItem,
  fetchLostFoundItems,
  restoreLostFoundItem,
} from "../../../services/LostFoundService";
import { useTranslation } from "react-i18next";
import "./LostFound.css";

const PAGE_SIZE = 8;

const formatDate = (value, locale) => {
  if (!value) return "—";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "—";
  try {
    return new Intl.DateTimeFormat(locale || "en", {
      year: "numeric",
      month: "short",
      day: "numeric",
    }).format(date);
  } catch {
    return date.toISOString().slice(0, 10);
  }
};

const truncateText = (value = "", max = 48) => {
  const text = String(value || "").trim();
  if (!text) return "—";
  if (text.length <= max) return text;
  return `${text.slice(0, max)}…`;
};

const formatItemCode = (id) => {
  if (!id) return "LF-0000";
  const hex = String(id).replace(/[^a-fA-F0-9]/g, "").slice(-4) || "0";
  const num = Number.parseInt(hex, 16) % 10000;
  return `LF-${String(num).padStart(4, "0")}`;
};

function LfConfirmModal({
  open,
  variant = "restore",
  title,
  message,
  confirmLabel,
  cancelLabel,
  item,
  busy,
  onConfirm,
  onClose,
  t,
}) {
  if (!open) return null;

  const isDelete = variant === "delete";

  return createPortal(
    <div className="lf-modal-backdrop" onClick={busy ? undefined : onClose}>
      <div
        className={`lf-modal lf-confirm ${isDelete ? "lf-confirm--danger" : "lf-confirm--restore"}`}
        onClick={(e) => e.stopPropagation()}
        role="dialog"
        aria-modal="true"
        aria-labelledby="lf-confirm-title"
      >
        <div className="lf-confirm__body">
          <span className="lf-confirm__icon" aria-hidden="true">
            <i className={`fa ${isDelete ? "fa-trash" : "fa-undo"}`} />
          </span>
          <h2 id="lf-confirm-title">{title}</h2>
          <p>{message}</p>

          {item && (
            <div className="lf-confirm__item">
              <div className="lf-thumb">
                {item.image ? (
                  <img
                    src={`${config.UPLOADS_SERVER}/lost-found/${item.image}`}
                    alt=""
                  />
                ) : (
                  <i className="fa fa-image" aria-hidden="true" />
                )}
              </div>
              <div className="lf-item-meta">
                <span className="lf-name">{item.title}</span>
                <span className="lf-code">{formatItemCode(item._id)}</span>
              </div>
            </div>
          )}
        </div>

        <div className="lf-confirm__foot">
          <button
            type="button"
            className="lf-modal__btn lf-modal__btn--ghost"
            onClick={onClose}
            disabled={busy}
          >
            {cancelLabel}
          </button>
          <button
            type="button"
            className={`lf-modal__btn ${isDelete ? "lf-modal__btn--danger" : "lf-modal__btn--restore"}`}
            onClick={onConfirm}
            disabled={busy}
          >
            {busy ? (
              <>
                <span className="lf-spinner lf-spinner--btn" aria-hidden="true" />
                {t("updating")}
              </>
            ) : (
              confirmLabel
            )}
          </button>
        </div>
      </div>
    </div>,
    document.body
  );
}

const ArchivedLostFound = () => {
  const { t, i18n } = useTranslation();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(false);
  const [search, setSearch] = useState("");
  const [statusFilter, setStatusFilter] = useState("");
  const [page, setPage] = useState(1);
  const [busyId, setBusyId] = useState("");
  const [confirm, setConfirm] = useState(null);

  const loadItems = async () => {
    setLoading(true);
    try {
      const data = await fetchLostFoundItems({ archived: true });
      setItems(Array.isArray(data) ? data : []);
    } catch (error) {
      toast.error(error?.message || t("lostFoundLoadError"));
      setItems([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadItems();
  }, []);

  useEffect(() => {
    setPage(1);
  }, [search, statusFilter]);

  const filteredItems = useMemo(() => {
    const term = search.trim().toLowerCase();
    return items.filter((item) => {
      const matchesStatus = !statusFilter || item.status === statusFilter;
      if (!matchesStatus) return false;
      if (!term) return true;
      const name = String(item.title || "").toLowerCase();
      const description = String(item.description || "").toLowerCase();
      return name.includes(term) || description.includes(term);
    });
  }, [items, search, statusFilter]);

  const totalPages = Math.max(1, Math.ceil(filteredItems.length / PAGE_SIZE));
  const currentPage = Math.min(page, totalPages);
  const paginatedItems = useMemo(() => {
    const start = (currentPage - 1) * PAGE_SIZE;
    return filteredItems.slice(start, start + PAGE_SIZE);
  }, [filteredItems, currentPage]);

  const closeConfirm = () => {
    if (busyId) return;
    setConfirm(null);
  };

  const runConfirm = async () => {
    if (!confirm?.item) return;
    const { type, item } = confirm;
    setBusyId(item._id);
    try {
      if (type === "restore") {
        await restoreLostFoundItem(item._id);
        toast.success(t("lostFoundRestored"));
      } else {
        await deleteLostFoundItem(item._id);
        toast.success(t("lostFoundDeleted"));
      }
      setConfirm(null);
      await loadItems();
    } catch (error) {
      toast.error(
        error?.response?.data?.message ||
          error?.message ||
          (type === "restore" ? t("lostFoundRestoreError") : t("lostFoundDeleteError"))
      );
    } finally {
      setBusyId("");
    }
  };

  return (
    <Fragment>
      <PageTitle activeMenu={t("navArchivedLostFound")} motherMenu={t("Archived")} />

      <div className="lf-page">
        <div className="lf-toolbar">
          <div>
            <h3 className="lf-title">{t("navArchivedLostFound")}</h3>
            <p className="lf-subtitle">
              <span className="lf-subtitle__dot" aria-hidden="true" />
              {t("lostFoundArchivedSubtitle")}
            </p>
          </div>
        </div>

        <div className="lf-card">
          <div className="lf-filters">
            <div className="lf-search">
              <i className="fa fa-search" aria-hidden="true" />
              <input
                type="text"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                placeholder={t("lostFoundSearchPlaceholder")}
              />
            </div>
            <select
              className="lf-input"
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value)}
            >
              <option value="">{t("lostFoundAllStatus")}</option>
              <option value="found">{t("lostFoundFound")}</option>
              <option value="returned">{t("lostFoundReturned")}</option>
            </select>
          </div>

          {loading ? (
            <div className="lf-loading">
              <span className="lf-spinner" aria-hidden="true" />
              {t("loading")}...
            </div>
          ) : (
            <>
              <div className="table-responsive">
                <table className="table lf-table mb-0">
                  <thead>
                    <tr>
                      <th>
                        <span className="lf-th">
                          <i className="fa fa-cube" aria-hidden="true" />
                          {t("lostFoundColItem")}
                        </span>
                      </th>
                      <th>
                        <span className="lf-th">
                          <i className="fa fa-calendar" aria-hidden="true" />
                          {t("lostFoundDate")}
                        </span>
                      </th>
                      <th>
                        <span className="lf-th">
                          <i className="fa fa-clock-o" aria-hidden="true" />
                          {t("lostFoundArchivedAt")}
                        </span>
                      </th>
                      <th>
                        <span className="lf-th">
                          <i className="fa fa-align-left" aria-hidden="true" />
                          {t("description")}
                        </span>
                      </th>
                      <th>
                        <span className="lf-th">
                          <i className="fa fa-flag" aria-hidden="true" />
                          {t("status")}
                        </span>
                      </th>
                      <th className="text-end">
                        <span className="lf-th lf-th--end">
                          <i className="fa fa-ellipsis-h" aria-hidden="true" />
                          {t("actions")}
                        </span>
                      </th>
                    </tr>
                  </thead>
                  <tbody>
                    {paginatedItems.length === 0 ? (
                      <tr>
                        <td colSpan={6} className="lf-empty">
                          <i className="fa fa-archive" aria-hidden="true" />
                          <span>{t("lostFoundArchivedEmpty")}</span>
                        </td>
                      </tr>
                    ) : (
                      paginatedItems.map((item) => {
                        const isReturned = item.status === "returned";
                        const busy = busyId === item._id;
                        return (
                          <tr
                            key={item._id}
                            className={isReturned ? "is-returned" : "is-found"}
                          >
                            <td>
                              <div className="lf-item-cell">
                                <div className="lf-thumb">
                                  {item.image ? (
                                    <img
                                      src={`${config.UPLOADS_SERVER}/lost-found/${item.image}`}
                                      alt={item.title}
                                    />
                                  ) : (
                                    <i className="fa fa-image" aria-hidden="true" />
                                  )}
                                </div>
                                <div className="lf-item-meta">
                                  <span className="lf-name">{item.title}</span>
                                  <span className="lf-code">{formatItemCode(item._id)}</span>
                                </div>
                              </div>
                            </td>
                            <td className="lf-date">
                              {formatDate(item.foundDate, i18n.language)}
                            </td>
                            <td className="lf-date">
                              {formatDate(item.archivedAt, i18n.language)}
                            </td>
                            <td className="lf-desc" title={item.description || ""}>
                              {truncateText(item.description)}
                            </td>
                            <td>
                              <div
                                className={`lf-status-wrap ${
                                  isReturned ? "is-returned" : "is-found"
                                }`}
                              >
                                <span className="lf-status-dot" aria-hidden="true" />
                                <span className="lf-status-label">
                                  {isReturned
                                    ? t("lostFoundReturned")
                                    : t("lostFoundFound")}
                                </span>
                              </div>
                            </td>
                            <td className="text-end">
                              <div className="lf-row-actions">
                                <button
                                  type="button"
                                  className="lf-icon-btn lf-icon-btn--restore"
                                  onClick={() => setConfirm({ type: "restore", item })}
                                  disabled={busy}
                                  aria-label={t("lostFoundRestore")}
                                  title={t("lostFoundRestore")}
                                >
                                  <i className="fa fa-undo" aria-hidden="true" />
                                </button>
                                <button
                                  type="button"
                                  className="lf-icon-btn lf-icon-btn--delete"
                                  onClick={() => setConfirm({ type: "delete", item })}
                                  disabled={busy}
                                  aria-label={t("delete")}
                                  title={t("delete")}
                                >
                                  <i className="fa fa-trash" aria-hidden="true" />
                                </button>
                              </div>
                            </td>
                          </tr>
                        );
                      })
                    )}
                  </tbody>
                </table>
              </div>

              {filteredItems.length > 0 && (
                <div className="lf-pagination">
                  <span>
                    {t("lostFoundShowingCount", {
                      shown: paginatedItems.length,
                      total: filteredItems.length,
                    })}
                  </span>
                  <div className="lf-pagination__btns">
                    {Array.from({ length: totalPages }, (_, i) => i + 1).map((n) => (
                      <button
                        key={n}
                        type="button"
                        className={`lf-page-btn${currentPage === n ? " is-active" : ""}`}
                        onClick={() => setPage(n)}
                      >
                        {n}
                      </button>
                    ))}
                  </div>
                </div>
              )}
            </>
          )}
        </div>
      </div>

      <LfConfirmModal
        open={Boolean(confirm)}
        variant={confirm?.type === "delete" ? "delete" : "restore"}
        title={
          confirm?.type === "delete"
            ? t("lostFoundDeleteTitle")
            : t("lostFoundRestoreTitle")
        }
        message={
          confirm?.type === "delete"
            ? t("lostFoundDeleteConfirm")
            : t("lostFoundRestoreConfirm", { item: confirm?.item?.title || "" })
        }
        confirmLabel={
          confirm?.type === "delete" ? t("delete") : t("lostFoundRestore")
        }
        cancelLabel={t("cancel")}
        item={confirm?.item}
        busy={Boolean(busyId)}
        onConfirm={runConfirm}
        onClose={closeConfirm}
        t={t}
      />
    </Fragment>
  );
};

export default ArchivedLostFound;
